import {
  Children,
  cloneElement,
  isValidElement,
  useRef,
  type ReactElement,
  type ReactNode
} from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import {
  assignRef,
  extractRef,
  mergeClasses,
  resolveTimeout,
  type TransitionDuration
} from "./vueTransitionHelpers";

// @ts-expect-error
type TransitionTag = keyof JSX.IntrinsicElements | null;

export type VueTransitionGroupProps = {
  children?: ReactNode;
  name?: string;
  tag?: TransitionTag;
  duration?: TransitionDuration;
  enterFromClass?: string;
  enterActiveClass?: string;
  enterToClass?: string;
  leaveFromClass?: string;
  leaveActiveClass?: string;
  leaveToClass?: string;
};

export function VueTransitionGroup({
  children,
  name = "v",
  tag = null,
  duration,
  enterFromClass,
  enterActiveClass,
  enterToClass,
  leaveFromClass,
  leaveActiveClass,
  leaveToClass
}: VueTransitionGroupProps): ReactElement | null {
  const nodeRefs = useRef(new Map<string, { current: Element | null }>());
  const getNodeRef = (key: string): { current: Element | null } => {
    const existing = nodeRefs.current.get(key);
    if (existing) return existing;
    const created = { current: null as Element | null };
    nodeRefs.current.set(key, created);
    return created;
  };

  const classNames = {
    enter: enterFromClass ?? `${name}-enter-from`,
    enterActive: mergeClasses(enterActiveClass ?? `${name}-enter-active`, enterToClass ?? `${name}-enter-to`),
    enterDone: "",
    exit: leaveFromClass ?? `${name}-leave-from`,
    exitActive: mergeClasses(leaveActiveClass ?? `${name}-leave-active`, leaveToClass ?? `${name}-leave-to`),
    exitDone: ""
  };

  const transitionChildren = Children.toArray(children)
    // @ts-expect-error
    .filter(child => child !== null && child !== undefined && child !== false)
    .map((child, index) => {
      if (!isValidElement(child)) return child;
      if (typeof child.type !== "string") return child;
      const childKey = String(child.key ?? `__vue-transition-group__${index}`);
      const nodeRef = getNodeRef(childKey);
      const existingRef = extractRef(child);
      const childWithRef = cloneElement(child, {
        // @ts-expect-error
        ref: (node: Element | null) => {
          nodeRef.current = node;
          assignRef(existingRef, node);
        }
      });
      return (
        <CSSTransition
          key={childKey}
          timeout={resolveTimeout(duration)}
          classNames={classNames}
          // @ts-expect-error
          nodeRef={nodeRef}
        >
          {childWithRef}
        </CSSTransition>
      );
    });

  if (transitionChildren.length === 0) return null;

  // @ts-expect-error
  return <TransitionGroup component={tag}>{transitionChildren}</TransitionGroup>;
}
